import {
  AmountPrice,
  AccountTypeCard,
  SectionTitle,
  SectionOfferings,
  TextBody,
  TextDisplay,
  TextParagraph,
  Button,
  TopGainersLosers,
  useBreakpoints,
  PageContainer,
  InfoPopover,
} from '@xbto/universal-components';
import { useAccountSelector } from '~/hooks/use-account-selector';
import { useWorkflowSelector } from '~/hooks/use-workflow-selector';
import { DataStore } from '~/store';
import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { APP_ROUTES } from '~/routes';
import { API } from '@xbto/api-client';
import { ActionId, useTrackEvent } from 'common';
import { useExtraNavCta } from '@xbto/universal-components';

export const ListOfAccounts_ExperienceFunds = () => {
  /**
   * Store
   */
  const totalBalanceUsd = DataStore.useStoreState(
    s => s.portfolio.totalBalanceUsd
  );
  const totalPnL = DataStore.useStoreState(s => s.portfolio.totalPnL);
  const accountsByProductName = DataStore.useStoreState(
    s => s.portfolio.accountsByProductName
  );
  // Note: Keep this data here to bind to table
  const gainersLosersData = DataStore.useStoreState(s => s.gainersLosers.data);
  const gainersLosersGetData = DataStore.useStoreActions(
    s => s.gainersLosers.getData
  );
  const allowedPortfolioActions = DataStore.useStoreState(
    s => s.portfolio.allowedActions
  );

  /**
   * Hooks
   */
  // TODO: The modal needs a header X close button as an option
  const navigate = useNavigate();
  const { breakpoints } = useBreakpoints();
  const { setExtraNavCta } = useExtraNavCta();
  const {
    Selector: AccountSelector,
    openByAccountType: openAccountSelectorByAccountType,
    openByWorklow: openAccountSelectorByWorkflow,
  } = useAccountSelector();
  const { Selector: WorkflowSelector, open: openWorkflowSelector } =
    useWorkflowSelector({
      canBuy: allowedPortfolioActions.includes(ActionId.Buy),
      canSell: allowedPortfolioActions.includes(ActionId.Sell),
      canSend: allowedPortfolioActions.includes(ActionId.Send),
      canReceive: allowedPortfolioActions.includes(ActionId.Receive),
      canTransfer: allowedPortfolioActions.includes(ActionId.Transfer),
      canAddCash: allowedPortfolioActions.includes(ActionId.AddCash),
      canWithdrawCash: allowedPortfolioActions.includes(ActionId.WithdrawCash),
      onPressWorkflow: openAccountSelectorByWorkflow,
    });
  useEffect(() => {
    gainersLosersGetData({
      type: 'gainers',
    });
  }, []);
  const trackEvent = useTrackEvent();

  /**
   * Methods
   */
  const onExploreMarkets = useCallback(() => {
    trackEvent('explore-markets_cta_clicked');
    navigate(APP_ROUTES.AUTH_MARKETS);
  }, [navigate, trackEvent]);
  const onToggleGainersLosers = useCallback(
    (type: 'gainers' | 'losers') => {
      gainersLosersGetData({
        type,
      });
    },
    [gainersLosersGetData]
  );
  const onProductTypePressed = useCallback(
    (type: API.AccountType) => {
      /**
       * Note:
       * There is no need to enumerate all account types here.
       * Because when we create this
       */
      if (type === API.AccountType.Fund) {
        trackEvent('fund_tile-item_clicked');
      }
      if (type === API.AccountType.ProTrading) {
        trackEvent('trading_tile-item_clicked');
      }
      if (type === API.AccountType.Custody) {
        trackEvent('custody_tile-item_clicked');
      }
      openAccountSelectorByAccountType(type);
    },
    [openAccountSelectorByAccountType, trackEvent]
  );
  const onEmptyProductTypePressed = useCallback(
    (type: API.AccountType) => {
      navigate(APP_ROUTES.AUTH_PORTFOLIO_PRODUCT_SPLASH + `?type=${type}`);
    },
    [navigate]
  );
  const addCta = useMemo(
    () => ({
      icon: 'Add' as const,
      variant: 'primary' as const,
      size: 'sm' as const,
      onPress: openWorkflowSelector,
    }),
    [openWorkflowSelector]
  );
  useEffect(() => {
    setExtraNavCta(breakpoints.LESS_THAN_M ? addCta : null);
    return () => {
      setExtraNavCta(null);
    };
  }, [breakpoints.LESS_THAN_M, addCta, setExtraNavCta]);

  /**
   * DOM
   */
  return (
    <PageContainer>
      <div className="flex flex-col">
        <div className="flex flex-row justify-between items-start py-6">
          <div className="flex flex-col gap-4">
            <TextBody lhFull>Total portfolio value</TextBody>
            <AmountPrice
              color="primary"
              as={TextDisplay}
              value={totalBalanceUsd}
              small={breakpoints.XS}
            />
            <TextParagraph>
              <div className="flex flex-row items-center gap-1">
                <TextBody>Total P&L (since account opened)</TextBody>
                <AmountPrice em value={totalPnL} signed />
                <InfoPopover
                  content="Includes both realized and unrealized gains/losses. Calculated using the latest known USD price for each asset."
                  placement="bottom-end"
                  maxWidth={240}
                />
              </div>
            </TextParagraph>
          </div>
          {breakpoints.MORE_THAN_M && <Button {...addCta} />}
        </div>
      </div>

      {/* Section: Cards */}
      <div className="flex flex-col py-8">
        <SectionTitle>Products</SectionTitle>
        <div className="flex flex-col flex-wrap gap-2 mt-6 lg:flex-row">
          {accountsByProductName?.map(acGroup => {
            return (
              <AccountTypeCard
                key={acGroup.type}
                type={acGroup.type}
                count={acGroup.count}
                value={acGroup.totalUsd}
                onPress={() => onProductTypePressed(acGroup.type)}
                onPressEmpty={onEmptyProductTypePressed}
                small={breakpoints.LESS_THAN_S}
              />
            );
          })}
        </div>
      </div>

      {/* Section: Top gainers/ losers */}
      <div className="flex flex-col w-full py-8">
        <TopGainersLosers
          rowData={gainersLosersData}
          onExploreMarkets={onExploreMarkets}
          onToggleType={onToggleGainersLosers}
          onRowPressed={row => {
            navigate(`${APP_ROUTES.AUTH_ASSET}/${row.code}`);
          }}
        />
      </div>

      {/* Section: Offering & other widgets */}
      <div className="flex flex-col w-full">
        <SectionOfferings />
      </div>

      {/* Section: Modals*/}
      <AccountSelector />
      <WorkflowSelector />
    </PageContainer>
  );
};
